import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { ReactNode, useEffect, useState } from 'react';
import { useFirmContext } from '../providers/FirmProvider';
import { useCustomWorksheetTemplate } from '../queries/use-custom-worksheet-template';
import { Spinner } from '@cimplico/components';
import ErrorSplashScreen from './ErrorSplashScreen';
import CWTDetails from '../features/custom-worksheet-template/CWTDetails';
import CWTTabs from '../features/custom-worksheet-template/CWTTabs';
import CWTCalculations from '../features/custom-worksheet-template/calculations/CWTCalculations';
import CWTCustomFunction from '../features/custom-worksheet-template/custom-functions/CWTCustomFunction';
import CWTConstants from '../features/custom-worksheet-template/constants/CWTConstants';
import CWTSettings from '../features/custom-worksheet-template/settings/CWTSettings';
import SchemaContainer from '../features/custom-worksheet-template/form/CWTSchemaContainer';
import { Provider, useDispatch } from 'react-redux';
import { store } from '../features/custom-worksheet-template/store/store';
import { CWTSchema } from '../types/custom-worksheet-v2';
import { loadSchema } from '../features/custom-worksheet-template/store/slice';
import { useIsFirmRole } from '../hooks/use-is-firm-role';

const tabs = ['fields', 'calculations', 'custom-functions', 'constants', 'settings'];

function getSavedTab(tab: string | null): number {
    if (!tab) {
        return 0;
    }

    return tabs.includes(tab) ? tabs.indexOf(tab) : 0;
}

function CustomWorksheetTemplatePage(): JSX.Element {
    const { shortId } = useFirmContext();
    const { id } = useParams();
    const { isExternalUser } = useIsFirmRole();
    const [params, setParams] = useSearchParams();
    const [currentTab, setCurrentTab] = useState(getSavedTab(params.get('tab')));

    useEffect(() => {
        setCurrentTab(getSavedTab(params.get('tab')));
    }, [params]);

    const cwtQuery = useCustomWorksheetTemplate(id);

    if (isExternalUser) {
        return <Navigate to={`/${shortId}/dashboard`} />;
    }

    if (cwtQuery.isLoading) {
        return (
            <div className="absolute w-full h-[var(--remaining-screen-height)]">
                <div className="absolute top-1/3 left-1/2">
                    <Spinner />
                </div>
            </div>
        );
    }

    if (cwtQuery.isError || !cwtQuery.isSuccess) {
        if (cwtQuery.error?.response?.status === 404) {
            return (
                <ErrorSplashScreen
                    errorCode={404}
                    title="Custom worksheet template not found."
                    redirectTitle="Back to custom worksheet templates"
                    redirectUrl={`/${shortId}/templates/worksheets`}
                    leftArrow={true}
                />
            );
        }
        return (
            <ErrorSplashScreen
                title="An error has occurred."
                redirectTitle="Back to custom worksheet templates"
                redirectUrl={`/${shortId}/templates/worksheets`}
                leftArrow={true}
            />
        );
    }

    function handleTabChange(t: number): void {
        setCurrentTab(t);
        setParams({ tab: tabs[t] });
    }

    const template = cwtQuery.data.template;

    return (
        <Provider store={store}>
            <LoadSchemaWrapper schema={template.schema}>
                <div className="flex flex-col gap-4">
                    <div className="px-8 pt-4 flex flex-col gap-4 bg-gray-50">
                        <CWTDetails template={template} />
                        <CWTTabs selectedTabIndex={currentTab} onTabChange={handleTabChange} />
                    </div>
                    <div className="mx-8">
                        {currentTab === 0 && <SchemaContainer templateName={template.name} />}
                        {currentTab === 1 && <CWTCalculations />}
                        {currentTab === 2 && <CWTCustomFunction />}
                        {currentTab === 3 && <CWTConstants />}
                        {currentTab === 4 && <CWTSettings />}
                    </div>
                </div>
            </LoadSchemaWrapper>
        </Provider>
    );
}

type LoadSchemaWrapperProps = {
    schema: CWTSchema;
    children: ReactNode | ReactNode[];
};

function LoadSchemaWrapper({ schema, children }: LoadSchemaWrapperProps): JSX.Element {
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(loadSchema(schema));
    }, [dispatch, schema]);

    return <>{children}</>;
}

export default CustomWorksheetTemplatePage;
